import React from "react";
import PropTypes from "prop-types";
import { Card, Col, Icon, message, Switch, Table, Tooltip } from "antd";
import { history } from "router";
import { CaseService } from "services";
import { formatCurrency } from "utilities/currency";
import "styles/group-info.css";

// Same tone mapping as the Existing Portfolio "Limit Info" table's status chip
// (portfolio-table.js statusTone), kept in sync so both surfaces read facility
// status identically.
function facilityStatusTone(value) {
    const v = String(value || "").toLowerCase();
    if (["closed", "rejected", "hold"].some(s => v.indexOf(s) !== -1)) {
        return "bad";
    }
    if (["active", "open"].some(s => v.indexOf(s) !== -1)) {
        return "good";
    }
    return "muted";
}

const groupInfoSanctionColumns = [
    { title: "Product Type", dataIndex: "business_product_type", width: 130 },
    { title: "Buyer GSTIN", dataIndex: "buyer_gstin", width: 150 },
    { title: "Seller Name", dataIndex: "seller_name", width: 150, render: _ => _ || "--" },
    {
        title: "Status",
        dataIndex: "facility_status",
        width: 100,
        render: _ =>
            _ ? (
                <span className={`group-info-status-chip group-info-status-chip--${facilityStatusTone(_)}`}>
                    {_}
                </span>
            ) : (
                "--"
            )
    },
    { title: "Throughput", dataIndex: "throughput", width: 130, render: _ => formatCurrency(_, false) },
    { title: "Sanctioned Limit", dataIndex: "sanction_limit", width: 130, render: _ => formatCurrency(_, false) },
    { title: "Proposed Limit", dataIndex: "proposed_limit", width: 130, render: _ => formatCurrency(_, false) },
    { title: "Adhoc Limit", dataIndex: "adhoc_limit", width: 120, render: _ => formatCurrency(_, false) },
    {
        title: "Adhoc Limit Expiry",
        dataIndex: "adhoc_limit_end_date",
        width: 130,
        render: v => {
            if (!v) {
                return "--";
            }
            const parts = String(v).slice(0, 10).split("-");
            return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(v);
        }
    },
    { title: "Current OS", dataIndex: "total_receivable", width: 120, render: _ => formatCurrency(_, false) },
    { title: "OD", dataIndex: "overdue_amount", width: 110, render: _ => formatCurrency(_, false) },
    // Sent as a percentage by the backend, not an amount.
    {
        title: "Blended Utilization",
        dataIndex: "blended_utilization",
        width: 140,
        render: v =>
            v == null || v === ""
                ? "--"
                : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`
    },
    { title: "# Churn", dataIndex: "churn", width: 100, render: _ => formatCurrency(_, false) },
    { title: "OD Invoices", dataIndex: "od_invoices", width: 110, render: _ => formatCurrency(_, false) },
    {
        title: <Tooltip title="Max DPD in LTM"><span>Max DPD (LTM)</span></Tooltip>,
        dataIndex: "max_dpd_12m",
        width: 110,
    },
    {
        title: <Tooltip title="Max DPD in LSM"><span>Max DPD (LSM)</span></Tooltip>,
        dataIndex: "max_dpd_6m",
        width: 110,
    },
    { title: "Max DPD", dataIndex: "max_dpd", width: 100 },
    {
        title: <Tooltip title="% of OD invoices"><span>OD Inv. %</span></Tooltip>,
        dataIndex: "prcn_od_invoices",
        width: 100,
        render: _ => formatCurrency(_, false),
    },
    {
        title: <Tooltip title="% of OD>30 dpd in LTM"><span>OD&gt;30 LTM %</span></Tooltip>,
        dataIndex: "prcn_od_30_ltm_invoices",
        width: 110,
        render: _ => formatCurrency(_, false),
    },
    { title: "LTM Disbursed", dataIndex: "ltm_disbursed", width: 120, render: _ => formatCurrency(_, false) },
    { title: "Total Repayment", dataIndex: "total_repayment", width: 130, render: _ => formatCurrency(_, false) },
    { title: "Tenure", dataIndex: "tenure", width: 100, render: _ => formatCurrency(_, true) },
    { title: "EMI Paid", dataIndex: "emi_paid", width: 100, render: _ => formatCurrency(_, true) },
    { title: "EMI Bounce", dataIndex: "emi_bounce", width: 110, render: _ => formatCurrency(_, true) },
];

// False as soon as ANY item is explicitly unvalidated; missing/null
// `is_validated` on an item does not count against it — only a literal
// `false` does. An empty list is vacuously all-validated.
export const isAllValidated = (items) =>
    !Array.isArray(items) || !items.some((item) => item && item.is_validated === false);

const getGroupInfoProductType = (record) => {
    const productType = record.sanction_data && record.sanction_data[0]
        ? record.sanction_data[0].business_product_type
        : null;
    if (!productType) {
        return "--";
    }
    const abbreviations = {
        "Channel Finance": "CF",
        "Term Loan": "TL",
        "Working Capital Demand Loan": "WCDL",
    };
    return abbreviations[productType] || productType;
};

class GroupInfoTable extends React.Component {
    state = {
        groupInfoData: [],
        groupInfoLoader: true,
        expandedGroupInfoKeys: [],
        expandedGroupInfoPillRows: {},
        groupInfoActiveToggling: {},
    };

    componentDidMount() {
        this.fetchGroupInfo();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.caseId !== this.props.caseId ||
            prevProps.filterByActive !== this.props.filterByActive
        ) {
            this.setState({
                expandedGroupInfoKeys: [],
                expandedGroupInfoPillRows: {},
                groupInfoActiveToggling: {},
            });
            this.fetchGroupInfo();
        } else if (prevProps.groupInfoRefreshKey !== this.props.groupInfoRefreshKey) {
            this.fetchGroupInfo();
        }
    }

    fetchGroupInfo = () => {
        const { caseId, onCountChange, onValidationChange } = this.props;
        if (!caseId) {
            return;
        }

        this.setState({ groupInfoLoader: true });
        CaseService.getCaseDedupe(caseId, this.props.filterByActive !== false)
            .then((res) => {
                const payload = Array.isArray(res?.data)
                    ? res.data
                    : (res?.data?.data || res?.data?.results || []);
                const rows = payload || [];
                this.setState({
                    groupInfoData: rows,
                    groupInfoLoader: false,
                });
                if (typeof onCountChange === "function") {
                    onCountChange(rows.length);
                }
                // Re-derived on every fetch (initial load, refresh-key bump,
                // post-toggle refetch) so a parent Approve button always
                // reflects the latest child rows, never a stale snapshot.
                if (typeof onValidationChange === "function") {
                    onValidationChange(isAllValidated(rows));
                }
            })
            .catch(() => {
                this.setState({ groupInfoLoader: false });
                if (typeof onCountChange === "function") {
                    onCountChange(0);
                }
            });
    };

    getPageId = () => window.location.pathname.split("/")[2] || "inbox";

    navigateToCaseCreditSummary = (caseId, event) => {
        if (event) {
            event.stopPropagation();
        }
        history.push(`/app/${this.getPageId()}/${caseId}/credit_summary`);
    };

    toggleGroupInfoPills = (rowId, event) => {
        event.stopPropagation();
        this.setState((prev) => ({
            expandedGroupInfoPillRows: {
                ...prev.expandedGroupInfoPillRows,
                [rowId]: !prev.expandedGroupInfoPillRows[rowId],
            },
        }));
    };

    handleGroupInfoActiveToggle = (record) => {
        const dedupeId = record.id;
        if (this.state.groupInfoActiveToggling[dedupeId]) {
            return;
        }

        this.setState((prev) => ({
            groupInfoActiveToggling: {
                ...prev.groupInfoActiveToggling,
                [dedupeId]: true,
            },
        }));

        CaseService.patchCaseDedupe(this.props.caseId, dedupeId)
            .then(() => {
                this.setState((prev) => ({
                    groupInfoActiveToggling: {
                        ...prev.groupInfoActiveToggling,
                        [dedupeId]: false,
                    },
                }));
                if (this.props.onGroupInfoUpdated) {
                    this.props.onGroupInfoUpdated();
                }
            })
            .catch(() => {
                message.error("Failed to update active status");
                this.setState((prev) => ({
                    groupInfoActiveToggling: {
                        ...prev.groupInfoActiveToggling,
                        [dedupeId]: false,
                    },
                }));
            });
    };

    renderGroupInfoActiveToggle = (record) => {
        const isActive = !!record.is_active;
        const isLoading = !!this.state.groupInfoActiveToggling[record.id];

        return (
            <div
                className="group-info-active-cell"
                onClick={(event) => event.stopPropagation()}
            >
                <span className={`group-info-active-status${isActive ? " group-info-active-status--yes" : " group-info-active-status--no"}`}>
                    {isActive ? "Yes" : "No"}
                </span>
                <Switch
                    className="group-info-active-switch"
                    checked={isActive}
                    loading={isLoading}
                    disabled={isLoading || this.props.disableActiveToggle}
                    onChange={() => this.handleGroupInfoActiveToggle(record)}
                />
            </div>
        );
    };

    renderMatchingIdPills = (matchingIds, record) => {
        if (!matchingIds) {
            return <span className="group-info-text-muted">--</span>;
        }
        const ids = matchingIds.split("|").filter(Boolean);
        if (!ids.length) {
            return <span className="group-info-text-muted">--</span>;
        }

        const PILL_LIMIT = 4;
        const showAll = this.state.expandedGroupInfoPillRows[record.id];
        const visibleIds = showAll ? ids : ids.slice(0, PILL_LIMIT);
        const hiddenCount = ids.length - PILL_LIMIT;

        return (
            <div className="group-info-pills-wrap" onClick={(event) => event.stopPropagation()}>
                {visibleIds.map((id) => (
                    <Tooltip key={id} title={`Open case ${id} credit summary`} mouseEnterDelay={0.4}>
                        <button
                            type="button"
                            className="group-info-case-pill"
                            onClick={(event) => this.navigateToCaseCreditSummary(id, event)}
                        >
                            <Icon type="link" className="group-info-case-pill-icon" />
                            <span>{id}</span>
                            <Icon type="right" className="group-info-case-pill-arrow" />
                        </button>
                    </Tooltip>
                ))}
                {!showAll && hiddenCount > 0 && (
                    <button
                        type="button"
                        className="group-info-pills-more"
                        onClick={(event) => this.toggleGroupInfoPills(record.id, event)}
                    >
                        +{hiddenCount} more
                    </button>
                )}
                {showAll && ids.length > PILL_LIMIT && (
                    <button
                        type="button"
                        className="group-info-pills-more"
                        onClick={(event) => this.toggleGroupInfoPills(record.id, event)}
                    >
                        Show less
                    </button>
                )}
            </div>
        );
    };

    renderDedupeType = (dedupeType) => {
        if (!dedupeType) {
            return <span className="group-info-text-muted">--</span>;
        }
        const types = dedupeType.split("|").map((t) => t.trim()).filter(Boolean);
        if (!types.length) {
            return <span className="group-info-text-muted">--</span>;
        }
        return (
            <div className="group-info-dedupe-tags">
                {types.map((type) => (
                    <span key={type} className="group-info-dedupe-tag">{type}</span>
                ))}
            </div>
        );
    };

    renderRelationship = (relationship) => {
        if (!relationship) {
            return <span className="group-info-text-muted">--</span>;
        }
        const isNeutral = relationship === "Buyer - Buyer";
        return (
            <span className={`group-info-relationship ${isNeutral ? "group-info-relationship--neutral" : "group-info-relationship--alert"}`}>
                {relationship}
            </span>
        );
    };

    renderProductTypeBadge = (record) => {
        const label = getGroupInfoProductType(record);
        if (label === "--") {
            return <span className="group-info-text-muted">--</span>;
        }
        return <span className="group-info-product-badge">{label}</span>;
    };

    getGroupInfoColumns = () => [
        {
            title: "Case ID",
            dataIndex: "case_id",
            width: 88,
            render: (caseId, record) => (
                <span>
                    {caseId}
                    {record.sanction_data && record.sanction_data.length > 0 && (
                        <span className="group-info-expand-hint">
                            <Icon type="down" style={{ fontSize: 9 }} />
                            details
                        </span>
                    )}
                </span>
            ),
        },
        {
            title: "Entity GSTIN",
            dataIndex: "entity_gstin",
            render: (gstin) => (
                gstin
                    ? <span className="group-info-gstin">{gstin}</span>
                    : <span className="group-info-text-muted">--</span>
            ),
        },
        {
            title: "Dedupe Type",
            dataIndex: "dedupe_type",
            render: (dedupeType) => this.renderDedupeType(dedupeType),
        },
        {
            title: "Relationship",
            dataIndex: "relationship",
            width: 130,
            render: (relationship) => this.renderRelationship(relationship),
        },
        {
            title: "Matching GSTIN",
            dataIndex: "matching_gstin",
            render: (gstin) => (
                gstin
                    ? <span className="group-info-gstin">{gstin}</span>
                    : <span className="group-info-text-muted">--</span>
            ),
        },
        {
            title: "Matching Name",
            dataIndex: "matching_name",
            render: (name) => (
                name
                    ? <span className="group-info-name" title={name}>{name}</span>
                    : <span className="group-info-text-muted">--</span>
            ),
        },
        {
            title: "Matching Cases",
            dataIndex: "matching_ids",
            width: 300,
            render: (matchingIds, record) => this.renderMatchingIdPills(matchingIds, record),
        },
        {
            title: "Product",
            key: "product_type",
            width: 100,
            align: "center",
            render: (_, record) => this.renderProductTypeBadge(record),
        },
        {
            title: "Active",
            dataIndex: "is_active",
            width: 72,
            align: "center",
            className: "group-info-active-col",
            render: (_, record) => this.renderGroupInfoActiveToggle(record),
        },
    ];

    renderGroupInfoExpandedRow = (record) => {
        if (!record.sanction_data || !record.sanction_data.length) {
            return null;
        }
        return (
            <div className="group-info-expanded-panel">
                <div className="group-info-expanded-header">
                    <Icon type="file-text" />
                    Sanction Details
                    {record.matching_name && (
                        <span className="group-info-expanded-subtitle">
                            — {record.matching_name}
                        </span>
                    )}
                </div>
                <div className="group-info-sanction-scroll">
                    <Table
                        bordered
                        size="small"
                        pagination={false}
                        scroll={{ x: 1560 }}
                        className="group-info-sanction-table"
                        rowKey={(_, index) => `${record.id}-sanction-${index}`}
                        columns={groupInfoSanctionColumns}
                        dataSource={record.sanction_data}
                    />
                </div>
            </div>
        );
    };

    renderGroupInfoExpandIcon = ({ expanded, onExpand, record }) => {
        if (!record.sanction_data || !record.sanction_data.length) {
            return <span className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" style={{ visibility: "hidden" }} />;
        }
        return (
            <span
                onClick={(event) => onExpand(record, event)}
                className={`ant-table-row-expand-icon ant-table-row-${expanded ? "expanded" : "collapsed"}`}
            />
        );
    };

    handleGroupInfoExpandedRowsChange = (expandedGroupInfoKeys) => {
        this.setState({ expandedGroupInfoKeys });
    };

    getGroupInfoRowClassName = (record) => {
        const classes = [];
        if (record.relationship !== "Buyer - Buyer") {
            classes.push("group-info-highlight-row");
        }
        if (record.sanction_data && record.sanction_data.length > 0) {
            classes.push("group-info-row-expandable");
        }
        return classes.join(" ");
    };

    render() {
        const { showSectionLabel } = this.props;
        const { groupInfoData, groupInfoLoader } = this.state;

        return (
            <Card
                className="case_detail_card card-align-center group-info-section"
                bodyStyle={{ padding: "12px 16px" }}
            >
                {showSectionLabel && (
                    <Col lg={2} sm={8}>
                        <p className="group-info-section-label">
                            Group Info
                            {!groupInfoLoader && groupInfoData.length > 0 && (
                                <span className="group-info-section-count">{groupInfoData.length}</span>
                            )}
                        </p>
                    </Col>
                )}
                <div className="d-flex w-100 group-info-content-col">
                    <Col lg={24} xs={24} sm={24} className="group-info-content-col">
                        <div
                            className={
                                "group-info-table-wrap" +
                                (!groupInfoLoader && groupInfoData.length === 0
                                    ? " group-info-table-wrap--empty"
                                    : "")
                            }
                        >
                            {/* Suppress the full column header row and the tall
                                empty-body block when there are no rows — a
                                compact one-line message is enough. */}
                            {!groupInfoLoader && groupInfoData.length === 0 ? (
                                <div className="group-info-empty-compact">
                                    No linked group entities found
                                </div>
                            ) : (
                                <Table
                                    bordered
                                    scroll={{ x: 1310 }}
                                    locale={{
                                        emptyText: (
                                            <span className="group-info-empty">
                                                {groupInfoLoader
                                                    ? "Loading group info..."
                                                    : "No linked group entities found"}
                                            </span>
                                        ),
                                    }}
                                    size="middle"
                                    columns={this.getGroupInfoColumns()}
                                    className="group-info-table ant-col padless-card w-100"
                                    dataSource={groupInfoData}
                                    loading={groupInfoLoader}
                                    pagination={false}
                                    rowKey="id"
                                    rowClassName={this.getGroupInfoRowClassName}
                                    expandedRowKeys={this.state.expandedGroupInfoKeys}
                                    onExpandedRowsChange={this.handleGroupInfoExpandedRowsChange}
                                    expandedRowRender={this.renderGroupInfoExpandedRow}
                                    expandIcon={this.renderGroupInfoExpandIcon}
                                    expandRowByClick
                                />
                            )}
                        </div>
                    </Col>
                </div>
            </Card>
        );
    }
}

GroupInfoTable.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    filterByActive: PropTypes.bool,
    showSectionLabel: PropTypes.bool,
    groupInfoRefreshKey: PropTypes.number,
    onGroupInfoUpdated: PropTypes.func,
    // Called with the row count each time the dedupe fetch completes — lets a
    // parent (e.g., the Existing Relationship section's Active/Inactive tabs)
    // show the count next to its tab label without re-fetching itself.
    onCountChange: PropTypes.func,
    // Called with isAllValidated(rows) each time this table (re)fetches, so a
    // parent Approve button can gate on the latest child validation state.
    onValidationChange: PropTypes.func,
    // Disables the per-row Active switch (e.g. outside the credit stage).
    disableActiveToggle: PropTypes.bool,
};

GroupInfoTable.defaultProps = {
    filterByActive: true,
    showSectionLabel: true,
};

export default GroupInfoTable;
